import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { productPath } from "../../lib/productUrls";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, ArrowUpRight, ImageIcon, Sparkles, Clock, TrendingUp } from "lucide-react";
import { pushRecentSearch, readRecentSearches, clearRecentSearches } from "../../lib/recentSearches";
import { inspectItem } from "../../lib/searchFields";
import { useDepartments } from "../../lib/queries";

// Trending search seeds for the empty-state. Curated for JnS Creation's
// boutique catalog — Anarkali / Saree / Festive / Wedding all map to either
// an actual category or a featured-collection slug in the storefront.
const TRENDING_SUGGESTIONS = [
    { label: "Anarkali Set", to: "/c/Anarkali%20Set" },
    { label: "Wedding", to: "/collection/wedding" },
    { label: "Festive", to: "/collection/festive" },
    { label: "Kurti", to: "/c/Kurti" },
    { label: "Saree", to: "/c/Saree" },
    { label: "Cord Set", to: "/c/Cord%20Set" },
];

/**
 * SmartSearch — boutique-style search popover used on the homepage and on
 * department / category landing pages.
 *
 * Shoppers see a pill input. As they type (≥ 2 chars) a results dropdown
 * appears below with up to 7 matched items (image + title + category +
 * price). Click → /item/<id>. Sticky footer button → /c/All?q=<term>
 * (the full results page that already exists).
 *
 * The dropdown closes on outside click / Esc / blur. Keyboard nav: ↑↓
 * moves selection, Enter opens the highlighted item, Enter on empty
 * selection navigates to the see-all page.
 */
export default function SmartSearch({
    items = [],
    placeholder = "Search…",
    minChars = 2,
    maxResults = 7,
    seeAllHref,            // override; default /c/All?q=<term>
    onSearchChange,        // optional — fires with current term so parent can filter
    autoFocus = false,
    "data-testid": testid = "smart-search",
}) {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);

    // Portal-positioned dropdown — anchored to the input via measured rect.
    // We use position:fixed + portal so the dropdown never gets clipped or
    // out-stacked by ancestors that have transform/filter/opacity (which
    // create new stacking contexts), e.g. Lookbook hover-zoom cards on the
    // homepage.
    const [anchor, setAnchor] = useState(null);  // {top, left, width}
    useLayoutEffect(() => {
        if (!open) return;
        const measure = () => {
            const el = wrapperRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            setAnchor({ top: r.bottom + 8, left: r.left, width: r.width });
        };
        measure();
        window.addEventListener("scroll", measure, true);
        window.addEventListener("resize", measure);
        return () => {
            window.removeEventListener("scroll", measure, true);
            window.removeEventListener("resize", measure);
        };
    }, [open]);

    // Re-emit q upwards (used by DepartmentLanding for live grid filtering)
    useEffect(() => {
        onSearchChange?.(q);
    }, [q]);  // eslint-disable-line react-hooks/exhaustive-deps

    const term = (q || "").trim().toLowerCase();
    const ready = term.length >= minChars;

    // The list of searchable fields + per-field match labels lives in
    // `lib/searchFields.js` so the dropdown and the /c/All?q=… results
    // page can never drift. Edit that module to add a new searchable
    // attribute everywhere at once.
    const { results, totalMatches } = useMemo(() => {
        if (!ready) return { results: [], totalMatches: 0 };
        const matched = [];
        for (const it of items) {
            const info = inspectItem(it, term);
            if (info.match) matched.push({ item: it, matchInfo: info });
        }
        return { results: matched.slice(0, maxResults), totalMatches: matched.length };
    }, [items, term, ready, maxResults]);

    // Department notation — with two verticals live (clothing + jewellery),
    // a free-text search can mix a saree and a pair of jhumkas with no way to
    // tell them apart. We surface a department pill on each result, but ONLY
    // when the shown results actually span >1 department (no noise on a
    // single-vertical search like "saree" or "jhumka"). Slug → display name
    // comes from the live Department docs so it reads "Jewellery", not the raw
    // slug, and picks up any future department automatically.
    const { data: departments = [] } = useDepartments();
    const deptNameBySlug = useMemo(() => {
        const m = {};
        for (const d of departments) if (d?.slug) m[d.slug] = d.name || d.slug;
        return m;
    }, [departments]);
    const multiDept = useMemo(() => {
        const s = new Set(results.map((r) => r.item.department_slug).filter(Boolean));
        return s.size > 1;
    }, [results]);

    // B2 analytics — track search queries (debounced, fires once when user
    // pauses typing for 800ms). Captures BOTH the query and the result count
    // so we can surface "queries that found 0 items" as a catalog gap signal.
    useEffect(() => {
        if (!ready) return;
        const t = setTimeout(() => {
            import("../../lib/analyticsBeacon").then(({ trackSearch }) => {
                trackSearch(term, totalMatches);
            }).catch(() => {});
        }, 800);
        return () => clearTimeout(t);
    }, [term, totalMatches, ready]);

    const [recents, setRecents] = useState(() => readRecentSearches());

    const seeAll = () => {
        pushRecentSearch(term);
        navigate(seeAllHref || `/c/All?q=${encodeURIComponent(term)}`);
    };

    // Record the term to "recent" when shopper clicks a hit too.
    const openHit = (it) => {
        pushRecentSearch(term);
        setOpen(false);
        navigate(productPath(it));
    };

    // Outside click + Esc to close. Since the dropdown is portaled, we
    // detect "inside" by checking BOTH the input wrapper AND any element
    // with our dropdown's data-testid (closest()).
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (wrapperRef.current?.contains(e.target)) return;
            if (e.target.closest && e.target.closest(`[data-testid="${testid}-dropdown"]`)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); } };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, testid]);

    const onKeyDown = (e) => {
        if (!open) return;
        // Empty-state — Enter on an empty input does nothing; user navigates via suggestion chips.
        if (!ready) return;
        const max = results.length;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, max - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlight >= 0 && results[highlight]) {
                openHit(results[highlight].item);
            } else if (totalMatches > 0) {
                seeAll();
            }
        }
    };

    return (
        <div ref={wrapperRef} className="relative" data-testid={testid}>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" size={16} />
            <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => {
                    setQ(e.target.value);
                    setHighlight(-1);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                autoFocus={autoFocus}
                aria-expanded={open}
                aria-autocomplete="list"
                data-testid={`${testid}-input`}
                className="w-full pl-11 pr-9 h-11 rounded-full
                           border border-stone-200 bg-white
                           shadow-[0_4px_16px_-8px_rgba(120,75,55,0.10)]
                           text-[16px] sm:text-[13.5px] text-ink placeholder:text-stone-400
                           focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/15
                           transition-all"
            />
            {q && (
                <button
                    type="button"
                    onClick={() => { setQ(""); setHighlight(-1); inputRef.current?.focus(); }}
                    data-testid={`${testid}-clear`}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-wine"
                    aria-label="Clear search"
                >
                    <X size={15} />
                </button>
            )}

            {/* Dropdown — rendered via portal so transforms/filters on
                ancestor sections (e.g. Lookbook hover-zoom) can't out-stack
                or clip it. Anchored to the input via measured fixed coords. */}
            {open && anchor && createPortal(
                <div
                    role="listbox"
                    data-testid={`${testid}-dropdown`}
                    style={{
                        position: "fixed",
                        top: anchor.top,
                        left: anchor.left,
                        width: anchor.width,
                        zIndex: 9999,
                    }}
                    className="bg-white rounded-2xl border border-stone-200
                               shadow-[0_24px_64px_-24px_rgba(120,75,55,0.30)]
                               overflow-hidden flex flex-col
                               max-h-[min(72vh,420px)] sm:max-h-[72vh]"
                >
                    {!ready ? (
                        <SuggestionsPanel
                            recents={recents}
                            onPick={(label) => {
                                pushRecentSearch(label);
                                setRecents(readRecentSearches());
                                setQ(label);
                                inputRef.current?.focus();
                            }}
                            onClearRecents={() => { clearRecentSearches(); setRecents([]); }}
                            testid={testid}
                        />
                    ) : results.length === 0 ? (
                        <EmptyState term={term} />
                    ) : (
                        <ul className="flex-1 overflow-y-auto py-2">
                            {results.map(({ item, matchInfo }, i) => (
                                <Hit
                                    key={item.id}
                                    item={item}
                                    matchInfo={matchInfo}
                                    active={i === highlight}
                                    deptLabel={multiDept ? (deptNameBySlug[item.department_slug] || item.department_slug) : null}
                                    onHover={() => setHighlight(i)}
                                    onClick={() => openHit(item)}
                                />
                            ))}
                        </ul>
                    )}
                    {/* Sticky footer CTA — only when shopper has typed a term */}
                    {ready && totalMatches > 0 && (
                        <button
                            type="button"
                            onClick={() => { setOpen(false); seeAll(); }}
                            data-testid={`${testid}-see-all`}
                            className="sticky bottom-0 w-full flex items-center justify-center gap-2
                                       px-4 py-3.5 border-t border-stone-200
                                       bg-ivory-soft text-wine text-[12px] font-bold uppercase tracking-[0.2em]
                                       hover:bg-rosegold hover:text-ivory transition-colors"
                        >
                            View all {totalMatches} matched item{totalMatches === 1 ? "" : "s"}
                            <ArrowUpRight size={13} />
                        </button>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
}

function Hit({ item, matchInfo, active, deptLabel, onHover, onClick }) {
    const photo = item.photo_url || (Array.isArray(item.photo_urls) && item.photo_urls[0]);
    const onSale = item.sale_price && item.sale_price < item.selling_price;
    const sizes = Array.isArray(item.available_sizes) && item.available_sizes.length > 0
        ? item.available_sizes
        : (typeof item.sizes === "string" ? item.sizes.split(",").map((s) => s.trim()).filter(Boolean) : []);
    const colorSwatch = colourHex(item.color || item.colour_family);

    // Build the "why this matched" chip row. We exclude `name` because if
    // the search term matched the title, it's already highlighted up top —
    // no need to repeat. Cap at 3 chips so the row stays one line.
    const reasonChips = (matchInfo?.fields || [])
        .filter((f) => f.label !== "name")
        .slice(0, 3);

    return (
        <li>
            <button
                type="button"
                onMouseEnter={onHover}
                onClick={onClick}
                data-testid={`hit-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                            ${active ? "bg-ivory-soft" : "hover:bg-ivory-soft/60"}`}
            >
                <div className="w-12 h-14 rounded-md overflow-hidden bg-ivory-soft flex-shrink-0 border border-stone-200">
                    {photo ? (
                        <img src={photo} alt="" className="w-full h-full object-cover object-top" loading="lazy" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-rosegold/40"><ImageIcon size={16} /></div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] font-medium text-ink truncate">{item.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {deptLabel && (
                            <span
                                className="text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-[1px] rounded-full bg-wine/10 text-wine border border-wine/20 flex-shrink-0"
                                data-testid={`hit-${item.id}-dept`}
                            >
                                {deptLabel}
                            </span>
                        )}
                        {item.category && (
                            <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-stone-500 truncate">{item.category}</span>
                        )}
                        {colorSwatch && (
                            <span
                                className="inline-flex items-center gap-1 text-[10px] text-ink-soft"
                                title={item.color || item.colour_family}
                            >
                                <span
                                    className="w-2.5 h-2.5 rounded-full border border-stone-300 flex-shrink-0"
                                    style={{ background: colorSwatch }}
                                />
                                <span className="lowercase">{(item.color || item.colour_family).toLowerCase()}</span>
                            </span>
                        )}
                        {sizes.length > 0 && (
                            <span className="text-[10px] text-ink-soft truncate">
                                · {sizes.slice(0, 4).join(" · ")}{sizes.length > 4 ? ` · +${sizes.length - 4}` : ""}
                            </span>
                        )}
                        {item.in_stock === false && (
                            <span className="text-[9px] uppercase tracking-wider font-bold text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">Sold out</span>
                        )}
                    </div>
                    {reasonChips.length > 0 && (
                        <div
                            className="flex items-center gap-1.5 mt-1 flex-wrap"
                            data-testid={`hit-${item.id}-reasons`}
                        >
                            <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-rosegold">
                                Matched
                            </span>
                            {reasonChips.map((c, i) => (
                                <span
                                    key={i}
                                    className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded-full bg-rosegold/15 text-wine border border-rosegold/40 text-[9.5px] font-semibold leading-tight"
                                    title={`${c.label}: ${c.value}`}
                                >
                                    <span className="opacity-75 uppercase tracking-wider">{c.label}</span>
                                    <span className="text-ink font-bold truncate max-w-[7rem]">
                                        {c.value}
                                    </span>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="text-right flex-shrink-0">
                    {onSale ? (
                        <>
                            <p className="text-[12.5px] font-bold text-wine">₹{(item.sale_price || 0).toLocaleString("en-IN")}</p>
                            <p className="text-[10px] text-stone-400 line-through">₹{(item.selling_price || 0).toLocaleString("en-IN")}</p>
                        </>
                    ) : (
                        <p className="text-[12.5px] font-bold text-ink">₹{(item.selling_price || item.cost_price || 0).toLocaleString("en-IN")}</p>
                    )}
                </div>
            </button>
        </li>
    );
}


// Light palette for the inline colour swatch on each search hit. Falls back
// to a soft warm-grey when the name is unknown so the chip stays visible
// without being misleading.
const COLOUR_MAP = {
    black: "#1c1917", white: "#fafaf9", ivory: "#f5f1ea", offwhite: "#f5f1ea", cream: "#efe6d5",
    beige: "#d6c4a8", tan: "#c9a47a", brown: "#7c5a3a", chocolate: "#5b3a22",
    red: "#b6303c", maroon: "#7a2138", wine: "#6b2c39", burgundy: "#5a1f2b",
    pink: "#e9a7b3", peach: "#f4c3a7", coral: "#e57b6e", rose: "#d28b9a", "rose gold": "#c9a47a",
    rosegold: "#c9a47a", "rose-gold": "#c9a47a", blush: "#f0c8c8",
    orange: "#e07a3e", mustard: "#d6a02e", yellow: "#f3c33b", gold: "#d4af37",
    green: "#3a7d44", olive: "#6a7042", emerald: "#0f5132", mint: "#a8d8b9", sage: "#9fb89a",
    blue: "#2b4a7b", navy: "#1f2c4c", "navy blue": "#1f2c4c", royal: "#1d3f8a", teal: "#1f6e7a",
    sky: "#7ab6e2", "sky blue": "#7ab6e2", denim: "#3a5a8c", indigo: "#3b3a72",
    purple: "#6e4b8a", lavender: "#b4a3d1", violet: "#5b3a7a", lilac: "#c2afd1",
    grey: "#9a958e", gray: "#9a958e", silver: "#cfcfcf",
    multi: "linear-gradient(45deg,#f4c3a7,#9fb89a,#7ab6e2,#d28b9a)",
};
function colourHex(name) {
    if (!name) return null;
    const k = String(name).trim().toLowerCase();
    if (COLOUR_MAP[k]) return COLOUR_MAP[k];
    // crude word-match: "peach pink" → first hit
    for (const word of k.split(/\s+/)) {
        if (COLOUR_MAP[word]) return COLOUR_MAP[word];
    }
    return null;
}

function EmptyState({ term }) {
    return (
        <div className="px-5 py-8 text-center" data-testid="smart-search-empty">
            <div className="w-10 h-10 mx-auto rounded-full bg-ivory-soft flex items-center justify-center mb-2">
                <Sparkles size={16} className="text-rosegold" />
            </div>
            <p className="text-[13px] text-ink font-semibold">No matches for "{term}"</p>
            <p className="text-[11.5px] text-ink-soft mt-1">Try a different keyword or browse the categories.</p>
        </div>
    );
}

/**
 * SuggestionsPanel — empty-state body for SmartSearch.
 *
 * Renders two rails:
 *   1. Recent searches  — pulled from localStorage; clicking a chip fills
 *      the input so the shopper can refine instead of restarting.
 *   2. Trending  — curated boutique chips that navigate directly to the
 *      relevant category or featured-collection page (one tap = browse).
 */
function SuggestionsPanel({ recents, onPick, onClearRecents, testid }) {
    const hasRecents = Array.isArray(recents) && recents.length > 0;
    return (
        <div className="px-5 py-5 space-y-5" data-testid={`${testid}-suggestions`}>
            {hasRecents && (
                <section data-testid={`${testid}-recents`}>
                    <header className="flex items-center justify-between mb-2.5">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-rosegold">
                            <Clock size={12} />
                            Recent
                        </div>
                        <button
                            type="button"
                            onClick={onClearRecents}
                            data-testid={`${testid}-recents-clear`}
                            className="text-[10px] uppercase tracking-[0.18em] font-semibold text-stone-400 hover:text-wine"
                        >
                            Clear
                        </button>
                    </header>
                    <div className="flex flex-wrap gap-1.5">
                        {recents.map((label) => (
                            <button
                                key={label}
                                type="button"
                                onClick={() => onPick?.(label)}
                                data-testid={`${testid}-recent-chip`}
                                className="px-3 h-7 rounded-full bg-ivory-soft border border-stone-200 text-[11.5px] text-ink hover:border-rosegold hover:text-wine transition-colors"
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            <section data-testid={`${testid}-trending`}>
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] uppercase tracking-[0.22em] font-bold text-rosegold">
                    <TrendingUp size={12} />
                    Trending
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {TRENDING_SUGGESTIONS.map((s) => (
                        <Link
                            key={s.label}
                            to={s.to}
                            data-testid={`${testid}-trending-chip`}
                            className="px-3 h-7 inline-flex items-center rounded-full bg-white border border-stone-200 text-[11.5px] text-ink hover:border-rosegold hover:text-wine transition-colors"
                        >
                            {s.label}
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
